import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  Loader2,
  Inbox,
  CheckCircle2,
  AlertTriangle,
  Info,
  FileText,
  RefreshCw,
  Filter,
  Send,
  User as UserIcon,
  Bell,
  ExternalLink,
  Zap,
  ShieldOff,
  PauseCircle,
  Flame,
} from "lucide-react";
import StudentSummaryDialog from "./StudentSummaryDialog";

const SEVERITY_STYLES = {
  info: "border-blue-200 bg-blue-50 text-blue-800",
  warn: "border-amber-200 bg-amber-50 text-amber-800",
  success: "border-emerald-200 bg-emerald-50 text-emerald-800",
  danger: "border-red-200 bg-red-50 text-red-800",
};

const SEVERITY_ICONS = {
  info: Info,
  warn: AlertTriangle,
  success: CheckCircle2,
  danger: AlertTriangle,
};

const PRIORITY_STYLES = {
  normal:  "border-neutral-300 bg-neutral-100 text-neutral-800",
  alta:    "border-orange-400 bg-orange-500 text-white",
  critica: "border-red-500 bg-red-600 text-white animate-pulse",
};

const PRIORITY_LABEL = {
  normal:  "NORMAL",
  alta:    "ALTA",
  critica: "CRÍTICA",
};

const DOC_STATUS_STYLES = {
  pendiente:  "bg-neutral-100 text-neutral-700",
  solicitado: "bg-amber-100 text-amber-800",
  recibido:   "bg-blue-100 text-blue-800",
  rechazado:  "bg-red-100 text-red-800",
};

const DOC_STATUS_LABEL = {
  pendiente:  "Pendiente",
  solicitado: "Solicitado",
  recibido:   "Recibido",
  rechazado:  "Rechazado",
};

const BLOCKING_CONTACT_STATUSES = new Set([
  "no_contactar",
  "baja_voluntaria",
  "no_interesado",
  "contacto_pausado",
]);

const CONTACT_STATUS_LABEL = {
  activo:              "Activo",
  no_interesado:       "No interesado",
  pendiente_decision:  "Pendiente decisión",
  contacto_pausado:    "Contacto pausado",
  no_contactar:        "No contactar",
  baja_voluntaria:     "Baja voluntaria",
};

function formatWhen(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("es-ES", { dateStyle: "short", timeStyle: "short" });
  } catch (_) {
    return iso;
  }
}

function ageBadge(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const hours = (Date.now() - then) / (1000 * 60 * 60);
  if (hours < 1) return "hace min.";
  if (hours < 24) return `hace ${Math.round(hours)}h`;
  const days = hours / 24;
  if (days < 7) return `hace ${Math.round(days)}d`;
  return `hace ${Math.round(days / 7)}sem`;
}

function EventCard({ event, onResolve, onOpenStudent, onResend, busy }) {
  const Icon = SEVERITY_ICONS[event.severity] || Info;
  const cls = SEVERITY_STYLES[event.severity] || SEVERITY_STYLES.info;
  const priority = event.priority || "normal";
  const priCls = PRIORITY_STYLES[priority] || PRIORITY_STYLES.normal;
  const isStale = (event.event_type || "").startsWith("kai.") && event.event_type.endsWith("_stale");
  const contactBlocked = BLOCKING_CONTACT_STATUSES.has(event.contact_status || "activo") || event.pause_automations;
  const requiresHuman = !!event.requires_human;
  const count = event.auto_reminders_count || 0;

  return (
    <div
      className={"rounded-2xl border p-4 " + cls + (priority === "critica" ? " ring-2 ring-red-400/60 " : "")}
      data-testid={`kai-event-${event.id}`}
    >
      <div className="flex items-start gap-3">
        <div className="h-9 w-9 rounded-full bg-white/70 flex items-center justify-center shrink-0">
          <Icon className="h-4 w-4" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center flex-wrap gap-2">
            <span className="font-bold text-sm">{event.label}</span>
            <span
              data-testid={`kai-event-priority-${event.id}`}
              className={"inline-flex items-center rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider " + priCls}
            >
              {priority === "critica" && <Flame className="h-3 w-3 mr-0.5" />}
              {PRIORITY_LABEL[priority] || priority}
            </span>
            {contactBlocked && (
              <span
                className="inline-flex items-center gap-0.5 rounded-full bg-neutral-900 text-white text-[10px] font-bold px-1.5 py-0.5"
                title={event.pause_automations ? "Automatizaciones pausadas" : `Contacto: ${event.contact_status}`}
                data-testid={`kai-event-blocked-${event.id}`}
              >
                {event.pause_automations ? <PauseCircle className="h-2.5 w-2.5" /> : <ShieldOff className="h-2.5 w-2.5" />}
                No contactar
              </span>
            )}
            {requiresHuman && (
              <span className="inline-flex items-center gap-0.5 rounded-full bg-red-600 text-white text-[10px] font-bold px-1.5 py-0.5">
                <Zap className="h-2.5 w-2.5" /> Requiere decisión
              </span>
            )}
            <span className="text-[10px] font-mono uppercase tracking-wider text-neutral-500">
              {event.event_type}
            </span>
          </div>
          <div className="text-xs text-neutral-700 mt-0.5 flex items-center gap-1.5 flex-wrap">
            <UserIcon className="h-3 w-3" />
            <span className="font-semibold text-neutral-900">{event.student_name || event.student_id}</span>
            <span className="text-neutral-400">·</span>
            <span>{formatWhen(event.at)}</span>
            <span className="text-neutral-400">·</span>
            <span className="italic">{ageBadge(event.at)}</span>
            {event.age_hours != null && (
              <>
                <span className="text-neutral-400">·</span>
                <span className="font-mono text-[10px]">{Math.round(event.age_hours)}h stale</span>
              </>
            )}
            {isStale && (
              <>
                <span className="text-neutral-400">·</span>
                <span className="text-[10px]">
                  Auto-recordatorios: <b>{count}/2</b>
                </span>
              </>
            )}
          </div>
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {isStale && !event.resolved && (
            <button
              type="button"
              onClick={() => onResend(event)}
              disabled={busy === event.id || contactBlocked || requiresHuman}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-emerald-600 hover:bg-emerald-700 disabled:bg-neutral-300 disabled:cursor-not-allowed text-white text-[11px] font-bold"
              data-testid={`kai-event-resend-${event.id}`}
              title={
                contactBlocked
                  ? "Contacto bloqueado"
                  : requiresHuman
                    ? "Máximo alcanzado — decide manualmente"
                    : "Reenviar recordatorio por WhatsApp"
              }
            >
              <Send className="h-3 w-3" /> Reenviar
            </button>
          )}
          <div className="flex gap-1.5">
            <button
              type="button"
              onClick={() => onOpenStudent(event.student_id, event.student_name)}
              className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-white/60 bg-white/60 hover:bg-white text-[11px] font-semibold"
              data-testid={`kai-event-open-${event.id}`}
            >
              <ExternalLink className="h-3 w-3" /> Ficha
            </button>
            {!event.resolved && (
              <button
                type="button"
                onClick={() => onResolve(event.id)}
                className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg bg-neutral-900 hover:bg-neutral-800 text-white text-[11px] font-semibold"
                data-testid={`kai-event-resolve-${event.id}`}
              >
                <CheckCircle2 className="h-3 w-3" /> Gestionado
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function PendingDocRow({ item, onOpenStudent, onRemind }) {
  const badge = DOC_STATUS_STYLES[item.status] || DOC_STATUS_STYLES.pendiente;
  const label = DOC_STATUS_LABEL[item.status] || item.status;
  const remindersCount = item.reminders_count || 0;
  return (
    <div
      className="rounded-xl border border-neutral-200 bg-white p-3 flex items-start gap-3"
      data-testid={`kai-pending-doc-${item.doc_id}`}
    >
      <div className="h-8 w-8 rounded-full bg-neutral-100 flex items-center justify-center shrink-0">
        <FileText className="h-4 w-4 text-neutral-500" />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-sm truncate">{item.doc_name}</span>
          <span className={"inline-flex items-center rounded-full px-2 py-0.5 text-[10px] font-bold " + badge}>
            {label}
          </span>
          {remindersCount > 0 && (
            <span className="inline-flex items-center gap-0.5 rounded-full bg-neutral-900 text-white text-[10px] font-bold px-1.5 py-0.5">
              <Bell className="h-2.5 w-2.5" /> {remindersCount}
            </span>
          )}
        </div>
        <div className="text-xs text-neutral-600 mt-0.5 flex items-center gap-1.5 flex-wrap">
          <UserIcon className="h-3 w-3" />
          <span className="font-semibold text-neutral-900">{item.student_name}</span>
          {item.last_reminder_at && (
            <>
              <span className="text-neutral-400">·</span>
              <span className="text-neutral-500">Último recordatorio: {ageBadge(item.last_reminder_at)}</span>
            </>
          )}
        </div>
      </div>
      <div className="flex gap-1.5 shrink-0">
        <button
          type="button"
          onClick={() => onRemind(item)}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-emerald-300 bg-emerald-50 hover:bg-emerald-100 text-emerald-800 text-[11px] font-semibold"
          data-testid={`kai-pending-remind-${item.doc_id}`}
        >
          <Send className="h-3 w-3" /> Recordar
        </button>
        <button
          type="button"
          onClick={() => onOpenStudent(item.student_id, item.student_name)}
          className="inline-flex items-center gap-1 h-8 px-2.5 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 text-neutral-700 text-[11px] font-semibold"
          data-testid={`kai-pending-open-${item.doc_id}`}
        >
          <ExternalLink className="h-3 w-3" /> Ficha
        </button>
      </div>
    </div>
  );
}

const FILTERS = [
  { key: "new", label: "Nuevos" },
  { key: "resolved", label: "Gestionados" },
  { key: "all", label: "Todos" },
];

const PRIORITY_FILTERS = [
  { key: "",        label: "Todas prioridades" },
  { key: "critica", label: "Solo críticas" },
  { key: "alta",    label: "Alta+" },
];

export default function KaiTrayTab() {
  const [tray, setTray] = useState({ events: [], pending_documents: [] });
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState("new");
  const [priorityFilter, setPriorityFilter] = useState("");
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [busy, setBusy] = useState(null); // id of event being acted on
  const [summary, setSummary] = useState({ new_events: 0, critical_events: 0, alta_events: 0, pending_docs_students: 0 });

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = new URLSearchParams({ status: filter, limit: "200" });
      if (priorityFilter) url.set("priority", priorityFilter);
      const [trayRes, sumRes] = await Promise.all([
        api.get(`/kai/tray?${url.toString()}`),
        api.get(`/kai/tray/summary`),
      ]);
      setTray({
        events: trayRes.data.events || [],
        pending_documents: trayRes.data.pending_documents || [],
      });
      setSummary(sumRes.data || {});
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, [filter, priorityFilter]);

  useEffect(() => { load(); }, [load]);

  const resolveEvent = async (eventId) => {
    try {
      await api.post(`/kai/events/${eventId}/resolve`, { note: "" });
      toast.success("Evento marcado como gestionado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const openStudent = (id, name) => {
    setSelectedStudent({ id, name });
  };

  const resendEvent = async (ev) => {
    setBusy(ev.id);
    try {
      const { data } = await api.post(`/students/${ev.student_id}/kai/resend/${ev.id}`, {
        channel: "whatsapp",
      });
      if (data.wa_url) {
        window.open(data.wa_url, "_blank", "noopener,noreferrer");
      }
      toast.success(`Recordatorio ${data.count}/${data.max} enviado`);
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setBusy(null);
    }
  };

  const remind = async (item) => {
    try {
      await api.post(`/students/${item.student_id}/documents/${item.doc_id}/remind`, {
        channel: "whatsapp",
      });
      // Open WhatsApp so the admin can actually deliver the message
      const phone = String(item.student_phone || "").replace(/[^\d]/g, "");
      const norm = phone.length === 9 ? "34" + phone : phone;
      const first = (item.student_name || "").split(" ")[0] || "";
      const msg = `Hola ${first}, te recordamos que necesitamos el documento: ${item.doc_name}. Gracias — Natur Autoescuela.`;
      const url = norm
        ? `https://wa.me/${norm}?text=${encodeURIComponent(msg)}`
        : `https://wa.me/?text=${encodeURIComponent(msg)}`;
      window.open(url, "_blank", "noopener,noreferrer");
      toast.success("Recordatorio registrado");
      load();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    }
  };

  const grouped = useMemo(() => {
    const g = {};
    for (const d of tray.pending_documents) {
      const k = d.student_id;
      if (!g[k]) g[k] = { student_name: d.student_name, docs: [] };
      g[k].docs.push(d);
    }
    return Object.entries(g).sort((a, b) => (b[1].docs.length - a[1].docs.length));
  }, [tray.pending_documents]);

  return (
    <div className="space-y-6" data-testid="kai-tray-tab">
      {/* Header */}
      <div>
        <div className="flex items-start flex-wrap gap-3">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-xl bg-orange-500 text-white flex items-center justify-center shrink-0">
              <Inbox className="h-5 w-5" />
            </div>
            <div>
              <h2 className="text-2xl font-bold leading-tight">Bandeja KAI</h2>
              <p className="text-sm text-neutral-500">Supervisión de eventos y documentación pendiente.</p>
            </div>
          </div>
          <button
            type="button"
            onClick={load}
            disabled={loading}
            className="ml-auto inline-flex items-center gap-1.5 h-9 px-3 rounded-lg border border-neutral-200 bg-white hover:bg-neutral-50 text-sm font-semibold disabled:opacity-40"
            data-testid="kai-tray-refresh"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
            Actualizar
          </button>
        </div>
      </div>

      {/* Counters */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
        <div className="rounded-xl border border-neutral-200 bg-white p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-neutral-500">Pendientes</div>
          <div className="text-2xl font-bold text-neutral-900" data-testid="kai-count-new">{summary.new_events}</div>
        </div>
        <div className="rounded-xl border border-red-200 bg-red-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-red-600">Críticas</div>
          <div className="text-2xl font-bold text-red-700" data-testid="kai-count-critical">{summary.critical_events}</div>
        </div>
        <div className="rounded-xl border border-orange-200 bg-orange-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-orange-600">Alta</div>
          <div className="text-2xl font-bold text-orange-700" data-testid="kai-count-alta">{summary.alta_events}</div>
        </div>
        <div className="rounded-xl border border-blue-200 bg-blue-50 p-3">
          <div className="text-[10px] uppercase tracking-wider font-bold text-blue-600">Alumnos con docs</div>
          <div className="text-2xl font-bold text-blue-700" data-testid="kai-count-docs-students">{summary.pending_docs_students}</div>
        </div>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-2 flex-wrap">
        <Filter className="h-4 w-4 text-neutral-400" />
        {FILTERS.map((f) => (
          <button
            key={f.key}
            type="button"
            onClick={() => setFilter(f.key)}
            data-testid={`kai-filter-${f.key}`}
            className={
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors " +
              (filter === f.key
                ? "border-orange-500 bg-orange-500 text-white"
                : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50")
            }
          >
            {f.label}
          </button>
        ))}
        <span className="mx-2 h-4 w-px bg-neutral-200" />
        {PRIORITY_FILTERS.map((p) => (
          <button
            key={p.key || "all"}
            type="button"
            onClick={() => setPriorityFilter(p.key)}
            data-testid={`kai-priority-${p.key || "any"}`}
            className={
              "inline-flex items-center gap-1.5 h-8 px-3 rounded-full border text-xs font-semibold transition-colors " +
              (priorityFilter === p.key
                ? (p.key === "critica" ? "border-red-500 bg-red-600 text-white" : (p.key === "alta" ? "border-orange-500 bg-orange-500 text-white" : "border-neutral-900 bg-neutral-900 text-white"))
                : "border-neutral-200 bg-white text-neutral-700 hover:bg-neutral-50")
            }
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Events */}
      <section>
        <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3">
          Eventos ({tray.events.length})
        </h3>
        {loading && tray.events.length === 0 ? (
          <div className="py-10 text-center">
            <Loader2 className="h-6 w-6 animate-spin text-orange-500 mx-auto" />
          </div>
        ) : tray.events.length === 0 ? (
          <div className="rounded-2xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500" data-testid="kai-events-empty">
            No hay eventos {filter === "new" ? "pendientes" : filter === "resolved" ? "gestionados" : ""}.
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="kai-events-list">
            {tray.events.map((e) => (
              <EventCard
                key={e.id}
                event={e}
                onResolve={resolveEvent}
                onOpenStudent={openStudent}
                onResend={resendEvent}
                busy={busy}
              />
            ))}
          </div>
        )}
      </section>

      {/* Pending documents grouped by student */}
      {filter !== "resolved" && (
        <section>
          <h3 className="text-xs font-bold uppercase tracking-wider text-neutral-500 mb-3">
            Documentación pendiente ({tray.pending_documents.length} documento{tray.pending_documents.length === 1 ? "" : "s"} en {grouped.length} alumno{grouped.length === 1 ? "" : "s"})
          </h3>
          {grouped.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-neutral-300 bg-white p-8 text-center text-sm text-neutral-500" data-testid="kai-pending-docs-empty">
              Toda la documentación está al día. ✅
            </div>
          ) : (
            <div className="space-y-4" data-testid="kai-pending-docs-list">
              {grouped.map(([sid, g]) => (
                <div key={sid} className="rounded-2xl border border-neutral-200 bg-neutral-50 p-3" data-testid={`kai-pending-group-${sid}`}>
                  <div className="flex items-center gap-2 mb-2 px-1">
                    <UserIcon className="h-4 w-4 text-neutral-500" />
                    <span className="font-bold text-sm">{g.student_name}</span>
                    <span className="text-xs text-neutral-500">
                      · {g.docs.length} documento{g.docs.length === 1 ? "" : "s"}
                    </span>
                  </div>
                  <div className="space-y-2">
                    {g.docs.map((d) => (
                      <PendingDocRow
                        key={d.doc_id}
                        item={d}
                        onOpenStudent={openStudent}
                        onRemind={remind}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}
        </section>
      )}

      {selectedStudent && (
        <StudentSummaryDialog
          student={selectedStudent}
          onClose={() => { setSelectedStudent(null); load(); }}
        />
      )}
    </div>
  );
}
